import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Crown, Heart, BookMarked, Sparkles, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "@/components/Seo";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const statusMeta = {
  owned: { label: "added", color: "text-emerald-600", bg: "bg-emerald-50", Icon: BookMarked },
  wishlist: { label: "wishlisted", color: "text-[#2563EB]", bg: "bg-blue-50", Icon: Sparkles },
  favorite: { label: "favorited", color: "text-[#E11D48]", bg: "bg-red-50", Icon: Heart },
};

export default function Feed() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const load = () => api.get("/feed/recent", { params: { limit: 40 } })
      .then((r) => { if (!cancel) setItems(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    load();
    const id = setInterval(load, 30000);
    return () => { cancel = true; clearInterval(id); };
  }, []);

  return (
    <div className="page-enter max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
      <Seo title="Community feed" description="Live activity from Cartonomie collectors." />
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-wider mb-2 inline-flex items-center gap-2">
            <span className="live-dot" /> Live · Community
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900 inline-flex items-center gap-3">
            <Activity className="w-9 h-9 text-[#E11D48]" /> {t("feed.title", "Community feed")}
          </h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            {t("feed.subtitle", "Real-time activity from public collectors. Refreshes every 30s.")}
          </p>
        </div>
        <div className="pill">{items.length} events</div>
      </motion.div>

      {loading ? (
        <div className="flat-card p-16 text-center text-slate-400 text-sm">Streaming…</div>
      ) : items.length === 0 ? (
        <div className="flat-card p-16 text-center">
          <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
          <div className="text-sm text-slate-600 mb-1">No public activity yet.</div>
          <div className="text-xs text-slate-400">{t("feed.empty", "Be the first — make your profile public in your account.")}</div>
        </div>
      ) : (
        <div className="flat-card overflow-hidden" data-testid="feed-list">
          <AnimatePresence>
            {items.map((it, idx) => {
              const meta = statusMeta[it.status] || statusMeta.owned;
              const Icon = meta.Icon;
              return (
                <motion.div
                  key={it.id}
                  layout
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ delay: Math.min(idx, 12) * 0.03 }}
                  data-testid={`feed-item-${it.id}`}
                  className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition"
                >
                  <span className="text-xs font-bold text-slate-400 w-5">{(idx + 1).toString().padStart(2, "0")}</span>
                  <Link to={`/cards/${it.card_id}`} className="block w-12 h-16 bg-slate-100 rounded-md overflow-hidden flex-shrink-0">
                    {it.snapshot?.image && (
                      <img src={it.snapshot.image} alt={it.snapshot.name} className="w-full h-full object-cover" />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      <Link to={`/u/${it.user?.id}`} className="font-semibold text-slate-900 hover:text-[#E11D48] inline-flex items-center gap-1">
                        {it.user?.display_name}
                        {it.user?.is_premium && <Crown className="w-3 h-3 text-[#F59E0B]" />}
                      </Link>
                      <span className={`mx-2 text-xs font-semibold ${meta.color} inline-flex items-center gap-1`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      <Link to={`/cards/${it.card_id}`} className="font-semibold text-[#2563EB] hover:underline truncate inline">
                        {it.snapshot?.name || it.card_id}
                      </Link>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{it.snapshot?.set_name}</div>
                  </div>
                  <div className="text-xs text-slate-400 flex-shrink-0">{timeAgo(it.added_at)}</div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
